import classNames from 'classnames';
import type { TFunction } from 'i18next';

import type { DataTableColumnDef } from '@/components/organisms/DataTable';

import type { SystemLabels } from '../systemLabel';

import { PenBandFigure } from './PenBandFigure';
import { PenNameCell } from './PenNameCell';
import type { PenComparisonRowData, PenFigureColumn } from './penComparisonCells';

import styles from './PenComparisonTable.module.scss';

const groupHeader = classNames(styles.header, styles.group, styles.groupStart);

/** A figure column; `groupStart` draws the boundary before it, as its header does. */
function figure(
    id: PenFigureColumn,
    header: string,
    groupStart = false,
    headerMeta: { headerTitle?: string; headerDescribedBy?: string } = {}
): DataTableColumnDef<PenComparisonRowData> {
    return {
        id,
        header,
        meta: {
            headerClassName: classNames(styles.header, { [styles.groupStart]: groupStart }),
            ...headerMeta,
            cellClassName: ({ original }) =>
                classNames(styles.cell, original.cells[id].className, original.cells[id].band, {
                    [styles.groupStart]: groupStart,
                }),
            cellTitle: ({ original }) => {
                const cell = original.cells[id];
                return cell.band && cell.title ? undefined : cell.title;
            },
        },
        cell: ({ row: { original } }) => {
            const cell = original.cells[id];
            return cell.band && cell.title ? (
                <PenBandFigure figure={cell.figure} title={cell.title} />
            ) : (
                cell.figure
            );
        },
    };
}

function group(
    id: string,
    label: string,
    columns: DataTableColumnDef<PenComparisonRowData>[]
): DataTableColumnDef<PenComparisonRowData> {
    return {
        id,
        header: () => <span className={styles.groupLabel}>{label}</span>,
        meta: { headerClassName: groupHeader },
        columns,
    };
}

interface PenComparisonColumnsInput {
    t: TFunction;
    /** The site's planning-system labels, resolved once by the table. */
    system: SystemLabels;
    /** Whether the table has the Pellet column; see PenComparisonTable. */
    fallthroughSupported: boolean;
    /** The note below the table that the weight ± sub-header points its description at. */
    weightIntervalNoteId: string;
}

/**
 * The columns in the owner's U10 order: the four grouped metrics side by side, then the single
 * columns, so no lone column splits the groups. Grouped, the headers name the metric once and
 * each sub-header only the source, which is what lets the 15 columns fit the panel instead of
 * scrolling six of them out of sight. The first cell of each group, and Feed vs plan after the
 * last one, carry the group boundary as their headers do, so each divider runs unbroken from
 * the header through every row.
 */
export function penComparisonColumns(
    input: PenComparisonColumnsInput
): DataTableColumnDef<PenComparisonRowData>[] {
    const { t, system, fallthroughSupported, weightIntervalNoteId } = input;
    const bioscope = t('feedingModule.bioscope');
    const interval = t('feedingModule.table.sub.interval');

    const columns: DataTableColumnDef<PenComparisonRowData>[] = [
        {
            id: 'pen',
            header: t('feedingModule.table.pen'),
            meta: {
                headerClassName: classNames(styles.header, styles.penColumn),
                cellClassName: classNames(styles.cell, styles.penColumn, styles.penName),
            },
            cell: ({ row: { original } }) => (
                <PenNameCell
                    pen={original.pen}
                    name={original.name}
                    warnings={original.warnings}
                    system={system}
                />
            ),
        },
        figure('fishCount', t('feedingModule.table.fishCount')),
        group('weight', t('feedingModule.table.group.weight'), [
            figure('weightPlan', t('feedingModule.systemPlan', { system: system.name }), true),
            figure('weightActual', t('feedingModule.systemActual', { system: system.name })),
            figure('weightBioscope', bioscope),
            // IPS 2.6.1: each interval sits beside the value it qualifies. Weight Bioscope is
            // the Kalman trend on a ready pen, so this ± has to say it belongs to the day's
            // estimate (IPS 7). A title reaches only pointer users, so the note below the
            // table says it too and describes it.
            figure('weightInterval', interval, false, {
                headerTitle: t('feedingModule.table.confWeightHint'),
                headerDescribedBy: weightIntervalNoteId,
            }),
        ]),
        group('fcr', t('feedingModule.table.group.fcr'), [
            figure('fcrPms', system.name, true),
            figure('fcrBioscope', bioscope),
            figure('fcrInterval', interval),
        ]),
        group('sfr', t('feedingModule.table.group.sfr'), [
            figure('sfrPms', system.name, true),
            figure('sfrBioscope', bioscope),
        ]),
        group('rgi', t('feedingModule.table.group.rgi'), [
            figure('rgiFeed', system.name, true),
            figure('rgiBioscope', bioscope),
        ]),
        // Closes the RGI group, so it takes the boundary the groups draw.
        figure('feedVsPlan', t('feedingModule.kpi.feedVsPlan'), true),
    ];
    if (fallthroughSupported) {
        columns.push(figure('pellet', t('feedingModule.table.fallthrough')));
    }
    return columns;
}
